import { useMemo, useState } from "react";
import { useDebounce } from "@reactuses/core";
import { Row, Col } from "react-bootstrap";
import PieChart from "../../core/blocks/PieChart";
import DetailedMetric from "../../core/blocks/DetailedMetric";
import { formatDatamartFilters } from "../../../utils/formatV2DatamartFilters";
import useHaloRank from "../../../hooks/useHaloRank";
import { ProductCell } from "../../../modules/merchandising/components/cells/ProductCell";
import { formatCurrency, formatNumber } from "../../../utils/formatNumber";
import MultiMetricChart from "./MultiMetricChart";
import getMetricSpec from "../../../modules/merchandising/utils/metrics";
import { useFetch } from "../../../hooks/api";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useGetV4ProductReportByProduct } from "../../../modules/perf_reports/api/product/v4ProductReportByProduct";
import { useDates } from "../../../dates/DatesContext";
import useSelectedMetrics from "./hooks/useSelectedMetrics";
import moment from "moment";
import { Tabs, TabList, Tab, TabPanel } from "../../core/basic/Tabs";
import { AdvertisingSection } from "./ProductAdvertisingSection";
import ProgressBar from "../../core/basic/ProgressBar";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { Button, buttonRecipe } from "../../core/basic/Button";
import AudienceForm from "../../../modules/amc/components/audience/AudienceForm";
import AdPlanModal from "../../core/blocks/AdPlan/AdPlanModal";
import { useCategoriesQuery } from "../../../hooks/useCategories";
import { useAppSelector } from "../../../redux/hooks";
import { Modal } from "../../core/basic/Modal";
import { css } from "../../../../styled-system/css";
import { useClientTable } from "../../table/tableFeatures";
import { createColumnBuilder } from "../../table/columnBuilder";
import { Table } from "../../table/Table";
import { RT8Pagination } from "../../table/Pagination";
import { SearchInput } from "../../fields/base/TextField";
import { HStack } from "../../../../styled-system/jsx";
import { useTranslation } from "react-i18next";

const DEFAULT_TAB = "details";

const Metrics = ({ salesNtb, salesRepeat, customerValue, brandCustomerValue, purchaseFreq, haloRank }) => {
  const { t } = useTranslation(["merchandising"]);
  return (
    <Col xs={12} className="d-flex justify-content-around mb-3 mt-5">
      <DetailedMetric
        center
        metric={{
          id: "cv",
          name: t(($) => $.merchandising["New to Brand Sales"]),
          format: "currency",
          accessor: (data) => data.value,
        }}
        data={{ value: salesNtb }}
      />
      <DetailedMetric
        center
        metric={{
          id: "cv",
          name: t(($) => $.merchandising["Repeat Sales"]),
          format: "currency",
          accessor: (data) => data.value,
        }}
        data={{ value: salesRepeat }}
      />
      <DetailedMetric
        center
        metric={{
          id: "cv",
          name: t(($) => $.merchandising["LTV"]),
          format: "currency",
          accessor: (data) => data.value,
        }}
        data={{ value: customerValue }}
      />
      <DetailedMetric
        center
        metric={{
          id: "brand_cv",
          name: t(($) => $.merchandising["Brand LTV"]),
          format: "currency",
          accessor: (data) => data.value,
        }}
        data={{ value: brandCustomerValue }}
      />
      <DetailedMetric
        center
        metric={{
          id: "purchase_frequency",
          name: t(($) => $.merchandising["Purchase Frequency"]),
          format: "number",
          accessor: (data) => data.value,
        }}
        data={{ value: purchaseFreq }}
      />
      <DetailedMetric
        center
        metric={{
          id: "halo_rank",
          name: t(($) => $.merchandising["Halo Rank"]),
          format: "number",
          decimalPlaces: 0,
          accessor: (data) => data.value,
        }}
        data={{ value: haloRank }}
      />
    </Col>
  );
};

const Details = ({
  marketPlace,
  productChartData,
  productTotals,
  metrics,
  group,
  productId,
  metricsPosition = "top",
}) => {
  const { selectedMetrics, toggleMetric } = useSelectedMetrics(
    ["orders_repeat", "orders_ntb", "sales_repeat", "sales_ntb"],
    "shopperinsightsProduct",
  );
  const { ranks: productRanks } = useHaloRank(marketPlace, {});

  const { customerValue, purchaseFreq, salesRepeat, salesNtb, brandCustomerValue } = useMemo(() => {
    if (productTotals?.length > 0) {
      const totals = productTotals?.[0];
      return {
        customerValue: totals?.customer_value,
        purchaseFreq: totals?.purchase_freq,
        salesRepeat: totals?.sales_repeat,
        salesNtb: totals?.sales_ntb,
        brandCustomerValue: totals?.brand_customer_value,
      };
    }
    return {};
  }, [productTotals]);

  const handleLegendChange = (e) => {
    const id = e.target?.options?.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const metricsElement = (
    <Metrics
      salesNtb={salesNtb}
      salesRepeat={salesRepeat}
      customerValue={customerValue}
      brandCustomerValue={brandCustomerValue}
      purchaseFreq={purchaseFreq}
      haloRank={productRanks?.[productId]}
    />
  );

  return (
    <>
      {metricsPosition === "top" && metricsElement}
      <Col xs={12} className="mb-5">
        {productChartData?.length > 0 && (
          <MultiMetricChart
            metrics={metrics}
            selectedMetrics={selectedMetrics}
            data={productChartData}
            group={group}
            handleLegendChange={handleLegendChange}
          />
        )}
      </Col>
      {metricsPosition === "bottom" && metricsElement}
    </>
  );
};

const columnHelper = createColumnBuilder();

const Insights = ({ marketPlace, productTotals, metrics }) => {
  const { t } = useTranslation(["merchandising"]);
  const relatedProducts = useMemo(() => {
    if (productTotals?.length > 0) {
      const lastRow = productTotals[0];
      return (
        lastRow?.also_bought?.reduce((agg, p) => {
          // merge all the also_bought products, sum product totals
          for (const [k, v] of Object.entries(p)) {
            agg[k] = (agg[k] ?? 0) + v;
          }
          return agg;
        }, {}) ?? {}
      );
    }
    return {};
  }, [productTotals]);

  const { salesRepeat, salesNtb } = useMemo(() => {
    if (productTotals?.length > 0) {
      const totals = productTotals?.[0];
      return {
        salesRepeat: totals?.sales_repeat,
        salesNtb: totals?.sales_ntb,
      };
    }
    return {};
  }, [productTotals]);

  const { relatedProductIds, productIdFilter } = useMemo(() => {
    const ids = Object.keys(relatedProducts);
    return {
      relatedProductIds: ids,
      productIdFilter: ids.length > 0 ? [{ id: "product_id", value: { op: "anyof", value: ids } }] : [],
    };
  }, [relatedProducts]);

  const [globalFilter, setGlobalFilter] = useState("");
  const debounceSearch = useDebounce(globalFilter, 500);

  const byProductOptionsGetter = useGetV4ProductReportByProduct();

  const relatedProductsQuery = useQuery({
    ...byProductOptionsGetter({
      pagination: { pageIndex: 0, pageSize: relatedProductIds.length || 1 },
      sorting: [],
      filters: productIdFilter,
      search: debounceSearch,
    }),
    enabled: relatedProductIds.length > 0,
    placeholderData: keepPreviousData,
  });

  const relatedProductsData = relatedProductsQuery.data?.data;

  const pieData = useMemo(() => {
    return [
      {
        name: t(($) => $.merchandising["New to Brand Sales"]),
        value: salesNtb,
      },
      {
        name: t(($) => $.merchandising["Repeat Sales"]),
        value: salesRepeat,
      },
    ];
  }, [salesNtb, salesRepeat, t]);

  const relatedProductsTable = useMemo(() => {
    if (relatedProductsData) {
      return relatedProductsData.map((p) => {
        const halo_orders = relatedProducts[p.product_id];
        return {
          ...p,
          halo_orders,
        };
      });
    }
    return [];
  }, [relatedProductsData, relatedProducts]);

  const maxHaloOrders = useMemo(() => {
    return Object.values(relatedProducts)?.sort((a, b) => b - a)?.[0] ?? 1;
  }, [relatedProducts]);

  const columns = useMemo(
    () => [
      columnHelper.accessor("product_title", {
        header: t(($) => $.merchandising["Products"]),
        enableHiding: false,
        cell: (props) => (
          <ProductCell
            value={props.getValue()}
            original={{
              product_id: String(props.row.original.product_id),
              sales_state: props.row.original.sales_state,
              stock_status: props.row.original.stock_status,
              inventory_state: props.row.original.inventory_state,
              asin: props.row.original.asin,
              sku: props.row.original.sku,
              tags: props.row.original.tags,
              pricing_plan__status: props.row.original.pricing_plan_status,
              cellContext: "merchandising",
            }}
            showInventoryBadge={false}
          />
        ),
        minSize: 400,
        size: 500,
      }),
      columnHelper.accessor("halo_orders", {
        header: t(($) => $.merchandising["Halo Orders"]),
        cell: ({ getValue }) => (
          <span className="d-flex align-items-center gap-3">
            {formatNumber(getValue())}
            <ProgressBar progress={(getValue() / maxHaloOrders) * 100} width={65} />
          </span>
        ),
        meta: {
          justify: "end",
        },
      }),
    ],
    [maxHaloOrders, t],
  );

  const table = useClientTable({
    data: relatedProductsTable,
    columns,
    getRowId: (row) => String(row.product_id),
    meta: {
      showSkeleton:
        relatedProductsQuery.isFetching && (!relatedProductsQuery.data || relatedProductsQuery.isPlaceholderData),
    },
    initialState: {
      pagination: { pageIndex: 0, pageSize: 5 },
      sorting: [{ id: "halo_orders", desc: true }],
    },
  });

  return (
    <>
      <Col xs={4}>
        <h5 className="text-center">{t(($) => $.merchandising["New to Brand vs. Repeat Sales"])}</h5>
        <PieChart
          formatter={(v) =>
            formatCurrency(v, {
              marketPlace: marketPlace.marketPlace,
            })
          }
          data={pieData}
          nameAccessor={(d) => d.name}
          accessor={(d) => d.value}
          colors={[metrics[0].color, metrics[1].color]}
        />
      </Col>
      <Col xs={8}>
        <h5 className="text-center">{t(($) => $.merchandising["Halo Orders"])}</h5>
        <HStack padding={"small"}>
          <SearchInput
            aria-label={t(($) => $.merchandising["Search products"])}
            isExpandable
            css={{ flex: "0 1 350px" }}
            value={globalFilter}
            onChange={setGlobalFilter}
          />
          <RT8Pagination className={css({ marginLeft: "auto" })} table={table} canSetPageSize={false} />
        </HStack>
        <Table table={table} />
      </Col>
    </>
  );
};

const ProductShopperInsights = ({ productId, product, marketPlace, showTabs = false }) => {
  const { t } = useTranslation(["merchandising"]);
  const [selectedTab, setSelectedTab] = useState(DEFAULT_TAB);
  const [selectedAudienceFormAsin, setSelectedAudienceFormAsin] = useState(false);
  const [adPlanOpen, setAdPlanOpen] = useState(false);

  const { group, start, end } = useDates();
  const channel = useAppSelector((state) => state.channel);

  const defaultFormData =
    selectedAudienceFormAsin ?
      {
        asins: [selectedAudienceFormAsin],
        lookalike_audience_expected_reach: "SIMILAR",
      }
    : {};

  const { data: categoriesData } = useCategoriesQuery(marketPlace.marketPlace, channel.currentChannel);

  const [filters, dimensions] = useMemo(() => {
    const filters = formatDatamartFilters({
      marketplace: marketPlace.marketPlace,
      product_id: productId,
      date__gte: start.format("YYYY-MM-DD"),
      date__lte: end.format("YYYY-MM-DD"),
    });
    return [filters, group];
  }, [productId, start, end, group, marketPlace]);

  const { data: productChartData } = useFetch(
    ["shopperingsights", "product", marketPlace.marketPlace, productId, dimensions, filters],
    "/api/data_report_v2/shopperinsights/stats/",
    {
      dimensions,
      filters,
    },
    {
      select: (res) => {
        return res.data.map((d) => ({ ...d, start_date: d.date })).sort((a, b) => moment(a.date) - moment(b.date));
      },
      default: [],
    },
  );

  const { data: productTotals } = useFetch(
    ["shopperingsights", "product", marketPlace.marketPlace, productId, "product_id", filters],
    "/api/data_report_v2/shopperinsights/stats/",
    {
      dimensions: "product_id",
      filters,
    },
    {
      select: (res) => {
        return res.data.map((d) => ({ ...d, start_date: d.date })).sort((a, b) => moment(a.date) - moment(b.date));
      },
      default: [],
    },
  );

  const metrics = useMemo(() => {
    return [
      getMetricSpec("sales_ntb"),
      getMetricSpec("sales_repeat"),
      getMetricSpec("orders_ntb"),
      getMetricSpec("orders_repeat"),
    ];
  }, []);

  const hasAMC = useModuleEnabled("AMC");

  return (
    <Row>
      {showTabs ?
        <>
          {hasAMC && (
            <Row>
              <Col xs={12} className="d-flex justify-content-end" style={{ marginBottom: "-36px" }}>
                <div style={{ marginRight: "4px" }} className="mb-5 d-flex gap-5">
                  <Modal
                    isOpen={selectedAudienceFormAsin}
                    onOpenChange={(isOpen) => {
                      if (!isOpen) {
                        setSelectedAudienceFormAsin(null);
                      }
                    }}
                    css={{
                      padding: "xlarge",
                      width: "60vw",
                    }}
                  >
                    <AudienceForm setShowAudienceForm={setSelectedAudienceFormAsin} defaultFormData={defaultFormData} />
                  </Modal>
                  <button className={buttonRecipe()} onClick={() => setAdPlanOpen(true)}>
                    {t(($) => $.merchandising["Create a New Media Plan"])}
                  </button>
                  <AdPlanModal
                    isOpen={adPlanOpen}
                    onClose={() => setAdPlanOpen(false)}
                    initialAdPlan={{
                      product: [product.id],
                      categoryId: product.product_category,
                      preselectedProducts: true,
                    }}
                    categories={categoriesData}
                  />
                  <Button onPress={() => setSelectedAudienceFormAsin(product.asin)}>
                    {t(($) => $.merchandising["Create Halo Audience"])}
                  </Button>
                </div>
              </Col>
            </Row>
          )}
          <Tabs selectedKey={selectedTab} onSelectionChange={setSelectedTab}>
            <TabList>
              <Tab id="details">{t(($) => $.merchandising["Details"])}</Tab>
              <Tab id="insights">{t(($) => $.merchandising["Insights"])}</Tab>
              <Tab id="advertising">{t(($) => $.merchandising["Advertising"])}</Tab>
            </TabList>
            <TabPanel id="details">
              <Row>
                <Details
                  marketPlace={marketPlace}
                  productChartData={productChartData}
                  productTotals={productTotals}
                  metrics={metrics}
                  group={group}
                  productId={productId}
                  metricsPosition="bottom"
                />
              </Row>
            </TabPanel>
            <TabPanel id="insights">
              <Row>
                <Insights
                  marketPlace={marketPlace}
                  productTotals={productTotals}
                  metrics={metrics}
                  productId={productId}
                />
              </Row>
            </TabPanel>
            <TabPanel id="advertising">
              <Row>
                <AdvertisingSection productId={productId} marketPlace={marketPlace} />
              </Row>
            </TabPanel>
          </Tabs>
        </>
      : <>
          <Details
            marketPlace={marketPlace}
            productChartData={productChartData}
            productTotals={productTotals}
            metrics={metrics}
            group={group}
            productId={productId}
          />
          <Insights marketPlace={marketPlace} productTotals={productTotals} metrics={metrics} productId={productId} />
        </>
      }
    </Row>
  );
};

export default ProductShopperInsights;
